// Core packages
import { formatDate } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { MatDialog, MatPaginator, MatSort } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { combineLatest, merge, Subscription } from "rxjs";
import {
  debounceTime, distinctUntilChanged, startWith, take, tap
} from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { PropertyTagNfcAddDialogComponent } from "src/app/modules/properties/components/property-tag-nfc-add-dialog/property-tag-nfc-add-dialog.component";
import { PropertyTagNfcEditDialogComponent } from 'src/app/modules/properties/components/property-tag-nfc-edit-dialog/property-tag-nfc-edit-dialog.component';
import { PropertyTagNfcReassignDialogComponent } from 'src/app/modules/properties/components/property-tag-nfc-reassign-dialog/property-tag-nfc-reassign-dialog.component';
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { PropertyTagNfcsService } from 'src/app/modules/properties/propertyTagNfcs.service';
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { Property } from "src/app/shared/models/property/property.model";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ListsService } from "src/app/shared/services/lists.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { PropertyTagNfcsDataSource } from "src/app/shared/utils/data-sources/property-tag-nfcs-data-source";

// Columns reserved to admin/operator users
const ADMIN_ONLY_TAG_NFC_COLUMNS = ["uid", "actions", "createdBy"];

@Component({
  selector: "app-property-property-tang-nfc",
  templateUrl: "./property-property-tang-nfc.page.html",
  styleUrls: ["./property-property-tang-nfc.page.scss"],
})
export class PropertyPropertyPropertyTangNfc implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  // Guards against wiring the table listeners more than once
  private propertyTagNfcTableInitialized = false;

  loggedUser: IUser;
  loggedUserPermissions: string[] = [];
  currentRole: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  // Could not be "Property" type because in "get" method in the back-end
  // We are populating some fields, so the model is not the same!
  public property: Property = new Property();
  propertyData: PropertyInterface;
  propertyId = this.activatedRoute.snapshot.params.propertyId;

  loggedUserShared = false;
  loggedUserAdmin = false;
  masterOnlySuppliers = false;
  propertyTagNfcCardIsLoading = true;

  // BEGIN - Table property Tag NFC
  propertyTagNfcsDisplayedColumns: string[] = [
    "index",
    "uid",
    "name",
    "shortDescription",
    "createdBy",
    "installationDate",
    "actions",
  ];
  propertyTagNfcsDataSource: PropertyTagNfcsDataSource;
  @ViewChild("propertyTagNfcSort", { static: false }) propertyTagNfcSort: MatSort;
  @ViewChild("propertyTagNfcPaginator", { static: false }) propertyTagNfcPaginator: MatPaginator;

  uidFilter: FormControl = new FormControl("");
  nameFilter: FormControl = new FormControl("");
  shortDescriptionFilter: FormControl = new FormControl("");
  installationDateFilter: FormControl = new FormControl("");
  createdByFilter: FormControl = new FormControl("");
  // END - Table property Tag NFC

  /**
   * Class constructor
   */
  constructor(
    public dialog: MatDialog,
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private propertiesService: PropertiesService,
    private router: Router,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    private cookieService: CookieService,
    private companiesService: CompaniesService,
    private listsService: ListsService,
    private propertyTagNfcsService: PropertyTagNfcsService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("properties.detail.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => {
        this.loggedUser = res;
        if (!this.loggedUser || !Array.isArray(this.loggedUser.userRoles)) {
          return;
        }
        this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
          return (
            obj._id.toString() ===
            this.authService.loggedUserRequestedRole$.getValue().toString()
          );
        });
        if (!this.currentRole || !this.currentRole.role) {
          return;
        }
        // Get logged user's role data
        if (this.currentRole.role.type === "shared") {
          this.loggedUserShared = true;
        }
        if (
          (this.currentRole.role.type === "master" ||
            this.currentRole.role.type === "collaborator") &&
          this.currentRole.company &&
          this.currentRole.company._id !== ""
        ) {
          this.subscriptions.push(
            this.companiesService
              .get(this.currentRole.company._id)
              .subscribe((companyRes) => {
                if (
                  companyRes.status &&
                  companyRes.data &&
                  companyRes.data.subscription
                ) {
                  const subscription = companyRes.data.subscription;
                  // Master whose subscription only includes suppliers:
                  // the Tag NFC card must not be shown
                  this.masterOnlySuppliers =
                    !subscription.documentUpload &&
                    !subscription.nfcAccess &&
                    !!subscription.suppliers;
                }
              })
          );
        }

        if (
          this.currentRole.role.type === "admin" ||
          this.currentRole.role.type === "operator"
        ) {
          this.loggedUserAdmin = true;
        } else {
          // Filter instead of splice(findIndex): on a later emission of
          // loggedUser$ findIndex returns -1 and splice(-1, 1) would drop
          // the last visible column
          this.propertyTagNfcsDisplayedColumns = this.propertyTagNfcsDisplayedColumns.filter(
            (column: string) => ADMIN_ONLY_TAG_NFC_COLUMNS.indexOf(column) === -1
          );
        }
      })
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = Array.isArray(res) ? res : [])
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Init data sources
    this.propertyTagNfcsDataSource = new PropertyTagNfcsDataSource(
      this.propertyTagNfcsService,
      this.helperService
    );

    // Get requested property data
    this.subscriptions.push(
      this.propertiesService
        .get(this.propertyId)
        .subscribe(
          (res) => {
            if (res.status) {
              this.property.setData(res.data);
              this.propertyData = JSON.parse(JSON.stringify(res.data));

              this.initPropertyTagNfcDataTable();
              this.propertyTagNfcCardIsLoading = false;
              return;
            }

            // Something bad happened
            // redirect user to home
            this.router.navigate([this.lang, "app", "properties", "list"]);
          },
          (err) => {
            // if we came here the requested property does not exist
            this.router.navigate([this.lang, "errors", "404"]);
          }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init Tag NFC data table: first load, sorting, paging and filters.
   * Skipped when the table is not rendered (card hidden by *ngIf).
   *
   * @since 1.0.0
   */
  initPropertyTagNfcDataTable(): void {
    if (
      this.propertyTagNfcTableInitialized ||
      !this.propertyTagNfcSort ||
      !this.propertyTagNfcPaginator
    ) {
      return;
    }
    this.propertyTagNfcTableInitialized = true;

    // Load first set of data
    this.loadPropertyTagNfcPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.propertyTagNfcSort.sortChange.subscribe(
        () => (this.propertyTagNfcPaginator.pageIndex = 0)
      )
    );

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.propertyTagNfcSort.sortChange, this.propertyTagNfcPaginator.page)
        .pipe(tap(() => this.loadPropertyTagNfcPage()))
        .subscribe()
    );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.uidFilter.valueChanges.pipe(startWith(null as string)),
        this.nameFilter.valueChanges.pipe(startWith(null as string)),
        this.shortDescriptionFilter.valueChanges.pipe(startWith(null as string)),
        this.installationDateFilter.valueChanges.pipe(startWith(null as string)),
        this.createdByFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged() // Eliminate duplicate values
        )
        .subscribe((res) => {
          // Load page only if at least one value is not null
          if (!res.every((value) => value === null)) {
            this.loadPropertyTagNfcPage();
          }
        })
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPropertyTagNfcPage(): void {
    if (!this.propertyTagNfcSort || !this.propertyTagNfcPaginator) {
      return;
    }
    const start = this.propertyTagNfcPaginator.pageIndex * this.propertyTagNfcPaginator.pageSize;
    const length = this.propertyTagNfcPaginator.pageSize;
    const search = {
      general: "",
      uid: this.uidFilter.value,
      name: this.nameFilter.value,
      shortDescription: this.shortDescriptionFilter.value,
      installationDate: this.installationDateFilter.value,
      createdBy: this.createdByFilter.value,
    };
    this.propertyTagNfcsDataSource.loadPropertyTagNfcItems(
      this.propertyId,
      start,
      length,
      this.propertyTagNfcSort.active,
      this.propertyTagNfcSort.direction,
      search
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFiltersPropertyTagNfc(): void {
    // Reset lists service data
    this.listsService.setListSetting("", 0, 10, {});

    // Reset tables' filters
    this.uidFilter.setValue(null);
    this.nameFilter.setValue(null);
    this.shortDescriptionFilter.setValue(null);
    this.installationDateFilter.setValue(null);
    this.createdByFilter.setValue(null);

    if (!this.propertyTagNfcSort || !this.propertyTagNfcPaginator) {
      return;
    }

    // Reset tables' sorting versus and column
    this.propertyTagNfcSort.direction = "asc";
    this.propertyTagNfcSort.active = "name";
    this.propertyTagNfcSort._stateChanges.next();

    // Reset paginator
    this.propertyTagNfcPaginator.pageIndex = 0;
    this.propertyTagNfcPaginator.pageSize = 10;

    // Reload table's data
    this.loadPropertyTagNfcPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilterPropertyTagNfc(filter: FormControl): void {
    filter.reset();
    this.loadPropertyTagNfcPage();
  }

  /**
   * Export the property Tag NFC list as xlsx
   *
   * @since 1.0.0
   */
  onExportPropertyTagNfc(): void {
    const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
    const fileName = `${this.translateService.instant(
      "properties.dialog.propertyTagNfcList.exportPropertyFileName"
    )} ${date}`;
    this.subscriptions.push(
      this.propertyTagNfcsService.getExportPropertyShort(this.propertyId).subscribe(
        (res) => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: "application/vnd.ms-excel",
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Add new Tag NFC
   *
   * @since 1.0.0
   */
  onAddTagNfc(): void {
    const dialogRef = this.dialog.open(PropertyTagNfcAddDialogComponent, {
      data: {
        propertyId: this.propertyId,
      },
      width: "800px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.loadPropertyTagNfcPage();
      });
  }

  /**
   * View and Edit Tag NFC
   *
   * @param element Tag NFC to view/edit
   */
  onViewEditTagNfc(element: any): void {
    const dialogRef = this.dialog.open(PropertyTagNfcEditDialogComponent, {
      data: element,
      width: "800px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.loadPropertyTagNfcPage();
      });
  }

  /**
   * Delete Tag NFC
   *
   * @param element Tag NFC to delete
   */
  onDeleteTagNfc(element: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant("propertyTagNfc.dialog.delete.message"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }

        this.deleteTagNfc(element);
      });
  }

  /**
   * Delete given Tag NFC.
   *
   * If it has already been used to record entrances/exits, the back-end
   * refuses the deletion: only an Admin may retry it, and only after
   * choosing another Tag NFC of the same property to move those records to
   * (@see onTagNfcReassignmentRequired).
   *
   * @param element Tag NFC to delete
   * @param [newTagNfcId] Replacement Tag NFC id, once chosen by an Admin
   */
  deleteTagNfc(element: any, newTagNfcId?: string): void {
    this.subscriptions.push(
      this.propertyTagNfcsService
        .delete(element._id, newTagNfcId)
        .subscribe(
          (res) => {
            if (res) {
              this.toastrService.success(res.message);
              this.loadPropertyTagNfcPage();
            }
          },
          (err) => {
            if (
              err &&
              err.status === 409 &&
              err.error &&
              err.error.code === "TAG_REASSIGNMENT_REQUIRED"
            ) {
              this.onTagNfcReassignmentRequired(element);
              return;
            }
            this.helperService.handleError(err);
          }
        )
    );
  }

  /**
   * A Tag NFC can't be deleted while it's still referenced by entrance/exit
   * records: fetch the property's other tags and, if the logged in user is
   * an Admin, let them pick one to move those records to before retrying
   * the deletion. Blocks entirely if no other tag exists for the property.
   *
   * @param element Tag NFC whose deletion is blocked
   */
  onTagNfcReassignmentRequired(element: any): void {
    if (!this.loggedUserAdmin) {
      this.toastrService.error(
        this.translateService.instant("propertyTagNfc.dialog.reassign.notAllowed")
      );
      return;
    }

    this.subscriptions.push(
      this.propertyTagNfcsService
        .getPropertyTagNFCList(this.propertyId, 0, 9999, "name", "asc")
        .subscribe(
          (res) => {
            if (!res.status) {
              this.helperService.handleError(res);
              return;
            }

            const otherTags = (res.data.items || []).filter(
              (tag: any) => tag._id !== element._id
            );
            if (!otherTags.length) {
              this.toastrService.error(
                this.translateService.instant("propertyTagNfc.dialog.reassign.noAlternative")
              );
              return;
            }

            const reassignDialogRef = this.dialog.open(PropertyTagNfcReassignDialogComponent, {
              data: {
                tags: otherTags,
              },
              width: "500px",
            });
            reassignDialogRef
              .beforeClosed()
              .pipe(take(1))
              .subscribe((newTagNfcId) => {
                if (!newTagNfcId) {
                  return;
                }
                this.deleteTagNfc(element, newTagNfcId);
              });
          },
          (err) => {
            this.helperService.handleError(err);
          }
        )
    );
  }
}
